import clsx from 'clsx'
import React, { useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import ContentBlock from '@/components-latest/primitives/ContentBlock'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components-latest/primitives/Controls/Select'
import ScrollArea from '@/components-latest/primitives/Scrollable'
import Text, { textVariants } from '@/components-latest/primitives/Text'

import Styles from './ChooseOfferDetails.module.scss'

type Props = {
  className?: string
  discountValue: string
  onDiscountValueChange: (discount: string) => void
  expirationValue: string
  onExpirationChange: (expirationValue: string) => void
}

const ChooseOfferDetails = ({
  className,
  discountValue,
  expirationValue,
  onDiscountValueChange,
  onExpirationChange
}: Props) => {
  const { t } = useTranslation()

  const percentsValues = useMemo(() => [5, 10, 15, 20, 30], [])

  const expirationValues = useMemo(
    () => ['UNLIMITED', '3h', '12h', '24h', '3d', '7d', '15d', '30d'],
    []
  )

  return (
    <ContentBlock className={clsx(className, Styles.contentBlock)}>
      <Text variant="title-2">{t('offers.offers-details.title')}</Text>
      <div className={Styles.selectWrapper}>
        <Text variant="heading-05">
          {t('offers.offers-details.amount-label')}
        </Text>
        <Select onValueChange={onDiscountValueChange} value={discountValue}>
          <SelectTrigger
            arrowVariant="default"
            className={clsx(
              Styles.selectTrigger,
              textVariants({ variant: 'nextBody' }),
              className
            )}>
            <SelectValue
              placeholder={t('offers.offers-details.amount-placeholder')}
              asChild>
              <Text>
                {discountValue}
                {t('offers.offers-details.percents-off')}
              </Text>
            </SelectValue>
          </SelectTrigger>
          <SelectContent>
            <ScrollArea viewportClassName={Styles.scrollArea}>
              {percentsValues.map(percentValue => (
                <SelectItem
                  key={percentValue}
                  textClassName={textVariants({ variant: 'nextSubtitle' })}
                  value={String(percentValue)}>
                  {percentValue}
                  {t('offers.offers-details.percents-off')}
                </SelectItem>
              ))}
            </ScrollArea>
          </SelectContent>
        </Select>
      </div>

      <div className={Styles.selectWrapper}>
        <Text variant="heading-05">
          {t('offers.offers-details.expiration-label')}
        </Text>
        <Select onValueChange={onExpirationChange} value={expirationValue}>
          <SelectTrigger
            arrowVariant="default"
            className={clsx(
              Styles.selectTrigger,
              textVariants({ variant: 'nextBody' }),
              className
            )}>
            <SelectValue
              placeholder={t('offers.offers-details.expiration-placeholder')}
              asChild>
              <Text>{t(`offers.offers-details.${expirationValue}`)}</Text>
            </SelectValue>
          </SelectTrigger>
          <SelectContent>
            <ScrollArea viewportClassName={Styles.scrollArea}>
              {expirationValues.map(expirationKey => (
                <SelectItem
                  key={expirationKey}
                  textClassName={textVariants({ variant: 'nextSubtitle' })}
                  value={String(expirationKey)}>
                  {t(`offers.offers-details.${expirationKey}`)}
                </SelectItem>
              ))}
            </ScrollArea>
          </SelectContent>
        </Select>
      </div>
    </ContentBlock>
  )
}

export default ChooseOfferDetails
